import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import Modal from './Modal';
import Icon from './Icon';

interface Reward {
  dayIndex: number;
  amount: string;
}

interface StreakData {
  lengthDays: number;
  rewards: Reward[];
  currentDay: number;
  claimedToday: boolean;
  nextDayIndex: number;
  canClaim: boolean;
}

interface ClaimResult {
  credited: string;
  currentDay: number;
  nextDayIndex: number;
  claimedToday: true;
}

const CONFETTI_COLORS = ['#14f1d1', '#fbbf24', '#38bdf8', '#f472b6', '#a3e635', '#fb923c'];

export default function DailyStreakCard() {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [data, setData] = useState<StreakData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [celebration, setCelebration] = useState<{ credited: string; currentDay: number } | null>(null);

  const fetchStreak = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await api.get<StreakData>('/streak');
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStreak(); }, [fetchStreak]);

  const amountFor = (dayIndex: number): string => {
    const r = data?.rewards.find(x => x.dayIndex === dayIndex);
    return r ? r.amount : '0.00';
  };

  const onClaim = async (dayIndex: number) => {
    if (!data?.canClaim || claiming || dayIndex !== data.nextDayIndex) return;
    setClaiming(true);
    try {
      const res = await api.post<ClaimResult>('/streak/claim');
      setCelebration({ credited: res.data.credited, currentDay: res.data.currentDay });
    } catch {
      setError(true);
    } finally {
      setClaiming(false);
    }
  };

  // After the celebration dismisses, refetch so the next day becomes the new
  // glowing tile (and the "Come back tomorrow" hint shows).
  const onCelebrationDone = useCallback(() => {
    setCelebration(null);
    fetchStreak();
  }, [fetchStreak]);

  return (
    <div style={{
      backgroundColor: colors.accentSoft,
      border: `1px solid ${colors.cardBorder}`,
      borderRadius: 20,
      padding: 16,
      marginBottom: 16,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <Icon name="calendar-check" size={16} color={colors.accentText} style={{ marginRight: 8 }} />
        <div style={{ fontSize: 16, fontWeight: 900, color: colors.text }}>Daily Streak</div>
        {data && data.claimedToday && (
          <div style={{
            fontSize: 11.5, fontWeight: 700, color: colors.accentText,
            marginLeft: 'auto', whiteSpace: 'nowrap',
          }}>
            Come back tomorrow
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <span style={{
            width: 24, height: 24,
            border: `2.5px solid ${colors.accentDark}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'aga-spin 0.7s linear infinite',
          }} />
        </div>
      ) : error || !data ? (
        <button
          onClick={fetchStreak}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            width: '100%', padding: '18px 0', minHeight: 44,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: colors.accentText, fontSize: 13, fontWeight: 700,
          }}
        >
          <Icon name="redo" size={12} color={colors.accentText} />
          Tap to retry
        </button>
      ) : (
        <div style={{
          display: 'flex', gap: 10,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          padding: '4px 2px',
          scrollbarWidth: 'none',
        }}>
          {Array.from({ length: data.lengthDays }, (_, i) => i + 1).map(dayIndex => {
            const claimed = dayIndex <= data.currentDay;
            const isClaimable = dayIndex === data.nextDayIndex && data.canClaim;
            return (
              <DayTile
                key={dayIndex}
                dayIndex={dayIndex}
                amount={amountFor(dayIndex)}
                claimed={claimed}
                isClaimable={isClaimable}
                disabled={claiming}
                colors={colors}
                onPress={() => onClaim(dayIndex)}
              />
            );
          })}
        </div>
      )}

      <CelebrationModal
        colors={colors}
        t={t}
        data={celebration}
        onDone={onCelebrationDone}
      />

      <style>{`
        @keyframes aga-streak-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes aga-streak-glow {
          0%, 100% { box-shadow: 0 0 6px 0 rgba(20,241,209,0.35); }
          50%      { box-shadow: 0 0 18px 4px rgba(20,241,209,0.85); }
        }
        @keyframes aga-coin-pop {
          0%   { transform: scale(0); }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes aga-confetti-fall {
          0%   { transform: translateY(-40px) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(420px) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function DayTile({
  dayIndex, amount, claimed, isClaimable, disabled, colors, onPress,
}: {
  dayIndex: number;
  amount: string;
  claimed: boolean;
  isClaimable: boolean;
  disabled: boolean;
  colors: ReturnType<typeof useTheme>['colors'];
  onPress: () => void;
}) {
  const tileBg = isClaimable ? colors.secondary : colors.card;
  const tileBorder = isClaimable ? colors.accent : colors.cardBorder;
  const coinBg = isClaimable ? colors.accent : claimed ? colors.cardBorder : colors.accentSoft;
  const coinIconColor = isClaimable ? '#020617' : claimed ? colors.textMuted : colors.accentText;
  const amountColor = isClaimable ? colors.textInverse : claimed ? colors.textMuted : colors.text;
  const dayLabelColor = isClaimable ? colors.accent : colors.textMuted;

  const tileStyle: CSSProperties = {
    width: 84,
    boxSizing: 'border-box',
    borderRadius: 16,
    border: `${isClaimable ? 2 : 1}px solid ${tileBorder}`,
    backgroundColor: tileBg,
    padding: '12px 6px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    opacity: claimed ? 0.6 : 1,
    flexShrink: 0,
    ...(isClaimable ? { animation: 'aga-streak-pulse 1.7s ease-in-out infinite, aga-streak-glow 1.7s ease-in-out infinite' } : {}),
  };

  const inner = (
    <>
      <div style={{ fontSize: 14, fontWeight: 900, color: amountColor, marginBottom: 8 }}>${amount}</div>
      <div style={{
        position: 'relative',
        width: 42, height: 42, borderRadius: 21,
        backgroundColor: coinBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 8,
      }}>
        <Icon name="coins" size={18} color={coinIconColor} />
        {claimed && (
          <div style={{
            position: 'absolute', bottom: -2, right: -2,
            width: 16, height: 16, borderRadius: 8,
            border: `1.5px solid ${colors.card}`,
            backgroundColor: colors.success,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="check" size={8} color="#fff" />
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, fontWeight: 800, color: dayLabelColor }}>Day {dayIndex}</div>
    </>
  );

  if (isClaimable) {
    return (
      <button
        aria-label={`Claim Day ${dayIndex}`}
        disabled={disabled}
        onClick={onPress}
        style={{ ...tileStyle, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit' }}
      >
        {inner}
      </button>
    );
  }

  return <div style={tileStyle}>{inner}</div>;
}

function CelebrationModal({
  colors, t, data, onDone,
}: {
  colors: ReturnType<typeof useTheme>['colors'];
  t: (key: string, opts?: Record<string, unknown>) => string;
  data: { credited: string; currentDay: number } | null;
  onDone: () => void;
}) {
  const visible = !!data;

  // Auto-dismiss after a brief celebration.
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(onDone, 3000);
    return () => clearTimeout(timer);
  }, [visible, onDone]);

  if (!data) return null;

  return (
    <Modal open={visible} onClose={onDone} card={false} backdropOpacity={0.72}>
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        {/* Falling confetti */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {Array.from({ length: 28 }, (_, i) => {
            const left = Math.random() * 100;
            const size = 7 + Math.round(Math.random() * 7);
            const rounded = Math.random() > 0.5;
            const delay = Math.random() * 0.5;
            const dur = 1.7 + Math.random() * 0.7;
            return (
              <span
                key={i}
                style={{
                  position: 'absolute',
                  top: -40,
                  left: `${left}%`,
                  width: size,
                  height: rounded ? size : size * 1.6,
                  borderRadius: rounded ? size / 2 : 2,
                  backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                  animation: `aga-confetti-fall ${dur}s ease-out ${delay}s forwards`,
                }}
              />
            );
          })}
        </div>

        <div style={{
          position: 'relative',
          width: '100%', maxWidth: 320,
          borderRadius: 24,
          border: `2px solid ${colors.accent}`,
          backgroundColor: colors.card,
          padding: '18px 24px 24px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          animation: 'aga-modal-in 200ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: 40,
            backgroundColor: colors.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 14,
            animation: 'aga-coin-pop 450ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}>
            <Icon name="coins" size={34} color="#020617" />
          </div>

          <div style={{ fontSize: 22, fontWeight: 900, color: colors.text, textAlign: 'center', marginBottom: 6 }}>
            {t('home.streak.claimedTitle', { day: data.currentDay, defaultValue: `Day ${data.currentDay} claimed!` })}
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: colors.accentText, textAlign: 'center', marginBottom: 20 }}>
            {t('home.streak.claimedSubtitle', { amount: data.credited, defaultValue: `$${data.credited} added to your wallet` })}
          </div>

          <button
            onClick={onDone}
            style={{
              alignSelf: 'stretch',
              padding: '14px 0', borderRadius: 999,
              backgroundColor: colors.accent, color: '#020617',
              border: 'none', cursor: 'pointer',
              fontSize: 15, fontWeight: 900, fontFamily: 'inherit',
            }}
          >
            {t('home.streak.claimedCta', { defaultValue: 'Awesome!' })}
          </button>
        </div>
      </div>
    </Modal>
  );
}
